import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Zap, Flame, Coins, PenLine, Users, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';
import {
  effectiveRoleFor,
  getActiveSpace,
  onSpaceChange,
} from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Time-of-day greeting — kept stable across personas so users build muscle
// memory.
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// ───────────────────────────────────────────────── shared honest states
// Aug 19 2026 — small presentational helpers so a hero card never shows a
// fabricated number while its backing fetch is loading or has failed. No
// component here invents data; every value comes straight from the two
// real endpoints (`/api/stats` and `/api/stats/detailed`) already used
// elsewhere in this file for the writer hero.
const CardSkeleton = () => (
  <div role="status" aria-label="Loading stats">
    <div className="h-4 w-24 bg-white/20 rounded mb-3 animate-pulse" />
    <div className="h-3 w-32 bg-white/10 rounded animate-pulse" />
    <span className="sr-only">Loading…</span>
  </div>
);

const CardError = ({ message, onRetry }) => (
  <div role="alert">
    <div className="flex items-center gap-2 mb-1">
      <AlertCircle className="w-4 h-4 text-white/90 shrink-0" aria-hidden="true" />
      <span className="text-white text-sm font-semibold">{message}</span>
    </div>
    {onRetry && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
        className="text-xs underline text-white/85 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 rounded"
      >
        Try again
      </button>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────── ReaderHero
// Data sources (both real, both already used elsewhere in this app):
//  - `stats`        — GET /api/stats            (library_routes.get_user_stats)
//                     confirmed fields used here: reading_streak, books_read
//  - `levelStats`    — GET /api/stats/detailed   (xp_routes.get_user_stats_detailed)
//                     confirmed fields used here: overall_level, level_tier,
//                     reading.{comprehension,velocity,analysis}.xp,
//                     listening.comprehension.xp
// `/api/stats` never returns `level`, `xp`, or `evolution_tier` — the
// previous version of this component read those non-existent fields and
// silently rendered "Level 1 / Novice / 0 XP" for every user. This version
// only reads fields the backend actually returns, and shows an explicit
// loading/error state instead of a guessed default when a fetch is pending
// or fails.
const ReaderHero = ({
  stats,
  statsLoading,
  statsError,
  onRetryStats,
  levelStats,
  levelStatsLoading,
  levelStatsError,
  onRetryLevelStats,
}) => {
  const navigate = useNavigate();

  // June 2 2026 — the three hero cards are tappable shortcuts to
  // /reader-journey/breakdown which shows the full XP ledger / streak
  // calendar / level ladder. Plain <button> keeps the gradient styling
  // and a single `data-testid` per card.
  const go = (type) => navigate(`/reader-journey/breakdown?type=${type}`);

  // Total XP is a plain sum of the four confirmed real substat XP values
  // returned by /api/stats/detailed. This is arithmetic over real numbers
  // (not a guessed or fabricated figure) and mirrors how the backend
  // itself derives `overall_level` from the same four substats.
  const totalXP = levelStats
    ? [
        levelStats.reading?.comprehension?.xp,
        levelStats.reading?.velocity?.xp,
        levelStats.reading?.analysis?.xp,
        levelStats.listening?.comprehension?.xp,
      ].reduce((sum, v) => sum + (Number(v) || 0), 0)
    : null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dashboard-hero-reader">
      {/* Level + tier — GET /api/stats/detailed */}
      <button
        type="button"
        onClick={() => go('level')}
        className="text-left bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-4 shadow-lg hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 min-h-[100px]"
        data-testid="reader-hero-level"
      >
        {levelStatsLoading ? (
          <CardSkeleton />
        ) : levelStatsError ? (
          <CardError message="Level unavailable" onRetry={onRetryLevelStats} />
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-300" aria-hidden="true" />
                <span className="text-white font-semibold">
                  Level {levelStats?.overall_level ?? '—'}
                </span>
              </div>
              <span className="text-indigo-200 text-sm">
                {levelStats?.level_tier || '—'}
              </span>
            </div>
            <p className="text-indigo-200 text-xs">Tap to view your level breakdown</p>
          </>
        )}
      </button>

      {/* Reading streak — GET /api/stats */}
      <button
        type="button"
        onClick={() => go('streak')}
        className="text-left bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 shadow-lg hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 min-h-[100px]"
        data-testid="reader-hero-streak"
      >
        {statsLoading ? (
          <CardSkeleton />
        ) : statsError ? (
          <CardError message="Streak unavailable" onRetry={onRetryStats} />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-yellow-300" aria-hidden="true" />
              <span className="text-white font-semibold">
                {stats?.reading_streak || 0} Day Streak
              </span>
            </div>
            <p className="text-orange-100 text-sm">
              {stats?.reading_streak > 0 ? 'Keep it going!' : 'Start reading today!'}
            </p>
          </>
        )}
      </button>

      {/* Total XP (sum of real substats) + books completed — GET
          /api/stats/detailed + GET /api/stats */}
      <button
        type="button"
        onClick={() => go('xp')}
        className="text-left bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 shadow-lg hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 min-h-[100px]"
        data-testid="reader-hero-xp"
      >
        {levelStatsLoading && statsLoading ? (
          <CardSkeleton />
        ) : levelStatsError && statsError ? (
          <CardError
            message="XP unavailable"
            onRetry={() => {
              onRetryLevelStats?.();
              onRetryStats?.();
            }}
          />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-yellow-300" aria-hidden="true" />
              {levelStatsLoading ? (
                <span className="h-4 w-20 bg-white/20 rounded animate-pulse inline-block" aria-label="Loading XP" />
              ) : levelStatsError ? (
                <span className="text-white/90 text-sm font-medium">XP unavailable</span>
              ) : (
                <span className="text-white font-semibold">
                  {(totalXP ?? 0).toLocaleString()} Total XP
                </span>
              )}
            </div>
            <p className="text-emerald-100 text-sm">
              {statsLoading
                ? 'Loading books…'
                : statsError
                ? 'Books completed unavailable'
                : `${stats?.books_read ?? 0} books completed`}
            </p>
          </>
        )}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────── WriterHero
const WriterHero = ({ writer, journey, onWriterRefresh }) => {
  const navigate = useNavigate();
  const level = writer?.writerLevel || 1;
  const tier = writer?.tierMeta?.title || writer?.writerEvolutionTier || 'Ink Novice';
  const tierHex = writer?.tierMeta?.hex || '#9CA3AF';
  // Banked XP in the current level + the threshold required to evolve to
  // the next level. May 31 2026 — the uniform 5,000 spec means every level
  // shows the same threshold, but we still read it from the server in case
  // the spec changes again.
  const xpIn = writer?.xpInCurrentLevel ?? writer?.writerXPInCurrentLevel ?? 0;
  const xpForNext = writer?.xpForNextLevel || 5000;
  const xpProgress = Math.min(100, (xpIn / xpForNext) * 100);
  const evolutionEligible = !!writer?.evolutionEligible;
  const totalXP = writer?.writerTotalXP || 0;

  const coins = writer?.coinBalance || 0;
  const gbp = writer?.gbpBalance ?? (coins * 0.015);
  const lifetimeCoins = journey?.total_coins_from_journey || 0;
  const followers = (writer?.audienceStats || {}).totalFollowers || 0;
  // "Published" should reflect what the scribe has actually shipped — NOT
  // just admin-approved items. Falls back through totalSubmissions if the
  // server hasn't refreshed the count yet.
  const published =
    (writer?.submissionStats || {}).totalSubmissions ??
    (writer?.submissionStats || {}).approvedTotal ??
    0;
  const audioMin = journey?.total_audio_minutes_uploaded || 0;

  // Evolve handler — POST /api/writer/evolve, then bubble up so the
  // parent can refetch /writer/stats and the bar recolors.
  const [evolving, setEvolving] = useState(false);
  const handleEvolve = useCallback(async () => {
    if (!evolutionEligible || evolving) return;
    setEvolving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/evolve`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Evolution failed.');
        return;
      }
      const newLv = data.writerLevel || data.new_level || level + 1;
      toast.success(`Evolved to Scribe Lv ${newLv}!`);
      onWriterRefresh?.();
    } catch (e) {
      toast.error('Network error during evolution.');
    } finally {
      setEvolving(false);
    }
  }, [evolutionEligible, evolving, level, onWriterRefresh]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dashboard-hero-writer">
      {/* Scribe Level + tier + Evolve CTA — June 2 2026: card is itself
          tappable and routes to /writer-journey/breakdown?type=level. The
          inline Evolve button stays in-card and uses stopPropagation so a
          click on it doesn't also navigate. */}
      <button
        type="button"
        onClick={() => navigate('/writer-journey/breakdown?type=level')}
        className="text-left rounded-xl p-4 shadow-lg border relative overflow-hidden hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        style={{
          background: `linear-gradient(135deg, ${tierHex}33 0%, #1e293b 70%)`,
          borderColor: `${tierHex}55`,
        }}
        data-testid="writer-level-card"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <PenLine className="w-5 h-5" style={{ color: tierHex }} />
            <span className="text-white font-semibold">Scribe Lv {level}</span>
          </div>
          <span className="text-xs font-bold" style={{ color: tierHex }}>{tier}</span>
        </div>
        <div className="w-full bg-slate-900/60 rounded-full h-2 mb-1">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{
              width: `${xpProgress}%`,
              background: `linear-gradient(90deg, ${tierHex}aa, ${tierHex})`,
            }}
          />
        </div>
        <p className="text-xs text-gray-300" data-testid="writer-xp-readout">
          {xpIn.toLocaleString()} / {xpForNext.toLocaleString()} XP
          {totalXP > xpIn && (
            <span className="text-gray-500 ml-1">· {totalXP.toLocaleString()} lifetime</span>
          )}
        </p>

        {evolutionEligible && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleEvolve(); }}
            disabled={evolving}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-md animate-pulse-evolve focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:opacity-60"
            style={{
              background: `linear-gradient(90deg, ${tierHex}, #f59e0b)`,
              boxShadow: `0 0 0 0 ${tierHex}66`,
            }}
            data-testid="writer-evolve-btn"
          >
            {evolving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Evolving…</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5" /> Evolve to Lv {level + 1}</>
            )}
          </button>
        )}
      </button>

      {/* Scribe coin balance + GBP earned */}
      <button
        type="button"
        onClick={() => navigate('/writer-journey/breakdown?type=coins')}
        className="text-left bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl p-4 shadow-lg hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        data-testid="writer-coins-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Coins className="w-5 h-5 text-white" />
          <span className="text-white font-semibold">
            {coins.toLocaleString()} Coins
          </span>
        </div>
        <p className="text-amber-100 text-sm">
          ≈ £{Number(gbp).toFixed(2)} cashable
          {lifetimeCoins > 0 && (
            <span className="block text-[11px] text-amber-50/90 mt-0.5">
              +{lifetimeCoins.toLocaleString()} earned from milestones
            </span>
          )}
        </p>
      </button>

      {/* Published output + audience */}
      <button
        type="button"
        onClick={() => navigate('/writer-journey/breakdown?type=audio')}
        className="text-left bg-gradient-to-br from-purple-600 to-fuchsia-600 rounded-xl p-4 shadow-lg hover:scale-[1.01] hover:shadow-xl transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        data-testid="writer-published-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-white" />
          <span className="text-white font-semibold" data-testid="writer-published-count">
            {published.toLocaleString()} Published
          </span>
        </div>
        <p className="text-purple-100 text-sm">
          {followers.toLocaleString()} follower{followers === 1 ? '' : 's'}
          {audioMin > 0 && (
            <span className="block text-[11px] text-purple-50/90 mt-0.5">
              {audioMin.toLocaleString()} audio min uploaded
            </span>
          )}
        </p>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────── Root
const DashboardHeader = ({ user, stats, statsError, subscription, onRetryStats }) => {
  // Persona resolution: scribes in writer-space + admins viewing as writer
  // see the WriterHero. Everyone else (incl. plain readers + scribes in
  // reader-space + admins viewing as reader/admin) see the ReaderHero.
  // Live-update when the user flips the TopBar persona toggle.
  const [, setTick] = useState(0);
  useEffect(() => onSpaceChange(() => setTick((n) => n + 1)), []);
  const effective = effectiveRoleFor(user?.role || 'user', getActiveSpace());
  const isWriter = effective === 'writer';

  // Scribe-only data — fetched lazily when persona = writer.
  const [writer, setWriter] = useState(null);
  const [journey, setJourney] = useState(null);
  // Bumping this triggers a refetch — used after Evolve.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!isWriter) return;
    let alive = true;
    (async () => {
      try {
        const [wr, jr] = await Promise.allSettled([
          authFetch(`${BACKEND_URL}/api/writer/stats`).then((r) => (r.ok ? r.json() : null)),
          authFetch(`${BACKEND_URL}/api/writer/journey`).then((r) => (r.ok ? r.json() : null)),
        ]);
        if (!alive) return;
        if (wr.status === 'fulfilled') setWriter(wr.value);
        if (jr.status === 'fulfilled') setJourney(jr.value);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { alive = false; };
  }, [isWriter, refreshTick]);

  // Reader-only detailed level/XP data — GET /api/stats/detailed, fetched
  // lazily when persona = reader. Kept separate from the parent's `stats`
  // fetch (GET /api/stats) since they're two distinct real endpoints with
  // independent loading/error lifecycles.
  const [levelStats, setLevelStats] = useState(null);
  const [levelStatsLoading, setLevelStatsLoading] = useState(false);
  const [levelStatsError, setLevelStatsError] = useState(false);
  const [levelRefreshTick, setLevelRefreshTick] = useState(0);

  useEffect(() => {
    if (isWriter) return;
    let alive = true;
    setLevelStatsLoading(true);
    setLevelStatsError(false);
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/stats/detailed`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setLevelStats(data);
      } catch {
        if (!alive) return;
        setLevelStatsError(true);
      } finally {
        if (alive) setLevelStatsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isWriter, levelRefreshTick]);

  // `stats` (from the parent's /api/stats fetch) has no separate loading
  // flag today — it starts `null` and is either populated or left `null`
  // on error. Treat "null + no error" as still loading so the streak card
  // shows a skeleton instead of a premature "0 Day Streak".
  const statsLoading = !stats && !statsError;

  // Premium badge — only rendered when the real /api/subscription/status
  // payload confirms an active paid tier. Free-tier / unloaded / errored
  // states simply omit the badge rather than guessing a plan name.
  const isPremium = !!subscription?.is_premium;
  const planLabel = subscription?.is_top_tier ? 'Premium+' : 'Premium';

  const greeting = (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-white">
          {getGreeting()}, {user?.name || user?.email?.split('@')[0] || 'Reader'}!
        </h1>
        {isPremium && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-300"
            data-testid="dashboard-subscription-badge"
          >
            <Crown className="w-3 h-3" aria-hidden="true" />
            {planLabel}
          </span>
        )}
      </div>
      <p className="text-gray-400 text-sm sm:text-base">
        {isWriter ? 'Keep building your audience' : 'Continue your learning journey'}
      </p>
    </div>
  );

  return (
    <div className="mb-8" data-testid="dashboard-header" data-persona={isWriter ? 'writer' : 'reader'}>
      {greeting}
      {isWriter ? (
        <WriterHero
          writer={writer}
          journey={journey}
          onWriterRefresh={() => setRefreshTick((t) => t + 1)}
        />
      ) : (
        <ReaderHero
          stats={stats}
          statsLoading={statsLoading}
          statsError={statsError}
          onRetryStats={onRetryStats}
          levelStats={levelStats}
          levelStatsLoading={levelStatsLoading}
          levelStatsError={levelStatsError}
          onRetryLevelStats={() => setLevelRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
};

export default DashboardHeader;
